import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { startTracking, stopTracking, trackNavigation } from '../services/portalTracking';

/**
 * Records one `navigation` / `page_view` portal event per screen the person
 * is on, INCLUDING the screen the layout mounts on.
 *
 * The previous inline effect in PortalLayout seeded its "previous path" ref
 * with the mount pathname and only recorded a view when the route CHANGED, so
 * the landing page (login redirect, bookmarked URL, reload) was never a row.
 * Anyone who lands on their one working screen and stays there (John Kirk on
 * /portal/tech-support-copilot: zero page views from 2026-09-11 on, while he
 * sent 80+ replies from that page) was invisible to page-view analytics, and
 * every session's first screen was missing.
 *
 * The landing view carries `landing: true` so analytics can tell "arrived
 * here" from "moved here". A re-render on the same path records nothing, which
 * also keeps React StrictMode's double effect run from double-counting.
 */
export function usePortalPageViews(): void {
  const location = useLocation();
  const lastRecordedPathRef = useRef<string | null>(null);

  useEffect(() => {
    startTracking();
    return () => stopTracking();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path === lastRecordedPathRef.current) return;
    const landing = lastRecordedPathRef.current === null;
    lastRecordedPathRef.current = path;
    trackNavigation(path, landing ? { landing: true } : undefined);
  }, [location.pathname]);
}
